// @ts-nocheck
import * as React from "react";

// Village 모듈 전용 경량 디자인 프리미티브. 기존 통합돌봄 common.tsx(1800+줄)와
// 격리해 유지보수성을 확보 — 토큰(--orange-500 등)은 전역 CSS 그대로 쓴다.

// ─── 색 토큰 접근자 (CSS 변수 폴백 포함) ─────────────────
export const TOKENS = {
  bg: "var(--bg, #FAF6EE)",
  bgSub: "var(--bg-sub, #F4EEDF)",
  surface: "var(--surface, #FFFFFF)",
  ink1: "var(--ink-1, #2A241F)",
  ink2: "var(--ink-2, #4F4740)",
  ink3: "var(--ink-3, #8A8079)",
  ink4: "var(--ink-4, #C6BDB1)",
  border: "var(--border, #E8DFC9)",
  borderSoft: "var(--border-soft, #F1E9D5)",
  orange500: "var(--orange-500, #E8825B)",
  orange600: "var(--orange-600, #D26A44)",
  orange50: "var(--orange-50, #FCEEE3)",
  green500: "var(--green-500, #6FA88F)",
  green600: "var(--green-600, #4F8A72)",
  green50: "var(--green-50, #E8F1EA)",
  rose500: "var(--rose-500, #C9594F)",
  rose50: "var(--rose-50, #F8E0DD)",
  amber500: "var(--amber-500, #D9A24A)",
  amber50: "var(--amber-50, #F9EEDA)",
  sky500: "var(--sky-500, #5C82A8)",
  sky50: "var(--sky-50, #E4ECF4)",
  shadowSm: "var(--shadow-sm, 0 1px 2px rgba(42,36,31,.04))",
  shadowMd: "var(--shadow-md, 0 4px 12px rgba(42,36,31,.06))",
};

// ─── Button ───────────────────────────────────────────────
export function Button({
  variant = "primary", size = "md", disabled, children, style, ...rest
}: {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  disabled?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  type?: "button" | "submit";
}) {
  const palette = {
    primary:   { bg: TOKENS.orange500, text: "#fff", border: TOKENS.orange500, hover: TOKENS.orange600 },
    secondary: { bg: TOKENS.surface,   text: TOKENS.ink1, border: TOKENS.border, hover: TOKENS.bgSub },
    ghost:     { bg: "transparent",    text: TOKENS.ink2, border: "transparent", hover: TOKENS.bgSub },
    danger:    { bg: TOKENS.rose500,   text: "#fff", border: TOKENS.rose500, hover: "var(--rose-600, #A8453C)" },
  }[variant];
  const pad = size === "sm" ? "6px 10px" : "9px 14px";
  const fs  = size === "sm" ? 12 : 13;
  return (
    <button
      {...rest}
      disabled={disabled}
      style={{
        padding: pad, borderRadius: 8, border: `1px solid ${palette.border}`,
        background: palette.bg, color: palette.text, fontSize: fs, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        transition: "background 120ms ease, transform 80ms ease",
        ...style,
      }}
      onMouseDown={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.transform = "translateY(1px)"; }}
      onMouseUp={(e)   => { (e.currentTarget as HTMLElement).style.transform = "none"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = "none"; }}
    >
      {children}
    </button>
  );
}

// ─── Card ─────────────────────────────────────────────────
export function Card({ children, style, onClick }: { children: React.ReactNode; style?: React.CSSProperties; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: TOKENS.surface, border: `1px solid ${TOKENS.border}`,
        borderRadius: 12, padding: 16, boxShadow: TOKENS.shadowSm,
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Badge ────────────────────────────────────────────────
export function Badge({ tone = "neutral", children }: { tone?: "neutral" | "primary" | "success" | "warning" | "danger" | "info"; children: React.ReactNode }) {
  const map = {
    neutral: { bg: TOKENS.bgSub, text: TOKENS.ink2 },
    primary: { bg: TOKENS.orange50, text: TOKENS.orange600 },
    success: { bg: TOKENS.green50, text: TOKENS.green600 },
    warning: { bg: TOKENS.amber50, text: "var(--amber-600, #B07F2E)" },
    danger:  { bg: TOKENS.rose50, text: TOKENS.rose500 },
    info:    { bg: TOKENS.sky50, text: TOKENS.sky500 },
  }[tone];
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      background: map.bg, color: map.text, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// ─── Field (label + input/select/textarea) ────────────────
export function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TOKENS.ink2, marginBottom: 4 }}>
        {label}{required && <span style={{ color: TOKENS.rose500, marginLeft: 2 }}>*</span>}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11, color: TOKENS.ink3, marginTop: 4 }}>{hint}</div>}
    </label>
  );
}

export const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", borderRadius: 8,
  border: `1px solid ${TOKENS.border}`, background: TOKENS.surface,
  color: TOKENS.ink1, fontSize: 13, fontFamily: "inherit",
};

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={{ ...inputStyle, ...(props.style || {}) }} />;
}
export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} style={{ ...inputStyle, minHeight: 72, resize: "vertical", ...(props.style || {}) }} />;
}
export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} style={{ ...inputStyle, ...(props.style || {}) }} />;
}

// ─── Modal ────────────────────────────────────────────────
export function Modal({ open, title, onClose, children, footer, width = 480 }: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000, display: "grid", placeItems: "center",
        background: "rgba(42,36,31,.35)", padding: 16,
      }}
    >
      <div style={{
        background: TOKENS.surface, borderRadius: 16, boxShadow: TOKENS.shadowMd,
        width: "100%", maxWidth: width, maxHeight: "90vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{
          padding: "14px 18px", borderBottom: `1px solid ${TOKENS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: TOKENS.ink1, margin: 0 }}>{title}</h2>
          <button
            aria-label="닫기"
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 20, color: TOKENS.ink3, cursor: "pointer", padding: 4 }}
          >×</button>
        </div>
        <div style={{ padding: 18, overflow: "auto" }}>{children}</div>
        {footer && (
          <div style={{
            padding: "12px 18px", borderTop: `1px solid ${TOKENS.border}`,
            display: "flex", gap: 8, justifyContent: "flex-end",
          }}>{footer}</div>
        )}
      </div>
    </div>
  );
}

// ─── Empty / Loading ──────────────────────────────────────
export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ textAlign: "center", padding: "32px 16px", color: TOKENS.ink3 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      {hint && <div style={{ fontSize: 12 }}>{hint}</div>}
    </div>
  );
}

export function Loading({ label = "불러오는 중…" }: { label?: string }) {
  return (
    <div style={{ textAlign: "center", padding: "24px 12px", color: TOKENS.ink3, fontSize: 13 }}>
      {label}
    </div>
  );
}

// ─── Toast (최소 구현) ────────────────────────────────────
type Toast = { id: number; text: string; tone: "info" | "success" | "danger" };
const ToastCtx = React.createContext<{ push: (text: string, tone?: Toast["tone"]) => void }>({ push: () => {} });

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [list, setList] = React.useState<Toast[]>([]);
  const push = React.useCallback((text: string, tone: Toast["tone"] = "info") => {
    const id = Date.now() + Math.random();
    setList((l) => [...l, { id, text, tone }]);
    window.setTimeout(() => setList((l) => l.filter((t) => t.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div style={{
        position: "fixed", top: 16, right: 16, zIndex: 2000, display: "grid", gap: 8,
      }}>
        {list.map((t) => (
          <div key={t.id} style={{
            padding: "10px 14px", borderRadius: 10, background: TOKENS.surface,
            boxShadow: TOKENS.shadowMd, fontSize: 13, color: TOKENS.ink1,
            borderLeft: `4px solid ${
              t.tone === "success" ? TOKENS.green500 :
              t.tone === "danger"  ? TOKENS.rose500  : TOKENS.sky500
            }`,
            maxWidth: 320,
          }}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return React.useContext(ToastCtx);
}

// 서버 응답 Error 를 한글 메시지로. apiErrorMessage 와 유사하지만 /design/api.ts
// 모듈 의존을 피하기 위해 Village 전용으로 둔다.
export function errorMessage(err: any): string {
  if (!err) return "오류가 발생했습니다.";
  const detail = err.details?.[0];
  if (detail) return String(detail);
  if (err.serverMessage) return String(err.serverMessage);
  if (err.code)          return `오류: ${err.code}`;
  return String(err.message || "오류가 발생했습니다.");
}
